import React, { FC, useState } from 'react';
import * as yup from 'yup';
import { Flex, Input, Link, Stack, Text } from '@chakra-ui/react';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Trans, useTranslation } from 'react-i18next';
import { nameRegExp, passwordRegExp, phoneRegExp } from '@/libs/utils';
import { AuthStatus, useAuthDispatch, useAuthState } from '@/contexts/AuthContext';
import { useAnalytics } from '@/contexts/AnalyticsContext';
import PhoneNumberInput from '@/components/PhoneNumberInput/PhoneNumberInput';
import { useColorModeValue as mode } from '@/components/ui/color-mode';
import { Field } from '@/components/ui/field';
import { Button } from '../ui/button';
import { Alert } from '../ui/alert';
import { signInWithRedirect } from 'aws-amplify/auth';

const SignUpForm: FC = () => {
  const { logEvent } = useAnalytics();
  const { signUp } = useAuthDispatch();
  const { sessionStatus, error } = useAuthState();
  const [showSignUpFields, setShowSignUpFields] = useState(false);
  const [checkingSSO, setCheckingSSO] = useState(false);
  const { t } = useTranslation('auth');
  const registerSchema = yup.object({
    password: showSignUpFields
      ? yup
          .string()
          .matches(passwordRegExp, {
            message: (cur) => {
              const fails: string[] = [];
              if (!cur.value.match(/[a-z]/)) {
                fails.push(t('validation.lowercase'));
              }
              if (!cur.value.match(/[A-Z]/)) {
                fails.push(t('validation.uppercase'));
              }
              if (!cur.value.match(/[0-9]/)) {
                fails.push(t('validation.numbers'));
              }
              if (!cur.value.match(/[!@#$%^&*\-_.]/)) {
                fails.push(t('validation.specialChars'));
              }
              if (cur.value.match(/^(?=.*\s)/)) {
                fails.push(t('validation.noSpaces'));
              }

              return t('validation.passwordRequirements', { fails: fails.join(', ') });
            }
          })
          .min(8, t('validation.passwordMinLength'))
          .required(t('validation.passwordRequired'))
      : yup.string().optional(),
    email: yup.string().email(t('validation.validEmail')).required(t('validation.emailRequired')),
    fName: showSignUpFields
      ? yup.string().matches(nameRegExp, t('validation.invalidName')).required(t('validation.firstNameRequired'))
      : yup.string().optional(),
    lName: showSignUpFields ? yup.string().matches(nameRegExp, t('validation.invalidName')) : yup.string().optional(),
    // .required('Last name is required'),
    phone: showSignUpFields
      ? yup.string().matches(phoneRegExp, t('validation.invalidPhone')).required(t('validation.phoneRequired'))
      : yup.string().optional()
  });
  const {
    handleSubmit,
    control,
    formState: { errors, isSubmitting },
    watch,
    trigger
  } = useForm({
    resolver: yupResolver(registerSchema),
    mode: 'all',
    defaultValues: {
      email: '',
      password: '',
      fName: '',
      lName: '',
      phone: ''
    }
  });

  const watchedEmail = watch('email');

  const initiateSSOLogin = async (provider: string) => {
    try {
      // const redirectUri = process.env.NEXT_PUBLIC_OAUTH_REDIRECT_SIGN_IN || 'http://localhost:3000/auth/callback/';

      await signInWithRedirect({
        provider: {
          custom: provider
        }
        // customState: redirectUri
      });
    } catch (err: any) {
      console.error('Failed to initiate SSO login:', err);
      // Fallback to password sign up on SSO error
      setShowSignUpFields(true);
    }
  };

  const checkForSSO = async (email: string) => {
    try {
      setCheckingSSO(true);
      const domain = email.split('@')[1];
      const response = await fetch(`${process.env.NEXT_PUBLIC_SSO_DOMAIN_LOOKUP_URL}?domain=${domain}`);
      const data = await response.json();

      if (data && data.providerName) {
        // SSO provider found, initiate SSO login
        await initiateSSOLogin(data.providerName);
        return true;
      }

      // No SSO provider, show sign up fields
      return false;
    } catch (err) {
      console.error('Error checking SSO:', err);
      // On error, fallback to standard sign up
      return false;
    } finally {
      setCheckingSSO(false);
    }
  };

  const onEmailContinue = async () => {
    const isValid = await trigger('email');
    if (!isValid) return;

    const hasSSO = await checkForSSO(watchedEmail);

    if (!hasSSO) {
      setShowSignUpFields(true);
    }
  };

  async function onSubmit(evt: any) {
    if (!showSignUpFields) {
      await onEmailContinue();
      return;
    }

    const { email, password, fName, lName, phone } = evt;

    await signUp({ email, fName, lName, password, phone });

    logEvent({
      action: 'signed_up',
      category: 'Sign up'
    });
  }

  const onBackToEmail = () => {
    setShowSignUpFields(false);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Stack gap={4}>
        {sessionStatus === AuthStatus.SIGN_UP_ERROR && (
          <Alert status='error' title={t('signUp.error')} colorPalette='brand'>
            {error?.message}
          </Alert>
        )}
        <Controller
          name='email'
          control={control}
          render={({ field }) => (
            <Field required label={t('signUp.email')} invalid={!!errors.email}>
              <Input
                {...field}
                required
                id='email'
                name='email'
                type='email'
                inputMode='email'
                autoComplete='email'
                autoCapitalize='none'
                spellCheck={false}
                placeholder={t('signUp.emailPlaceholder')}
                disabled={showSignUpFields}
              />
            </Field>
          )}
        />

        {showSignUpFields && (
          <>
            <Controller
              name='password'
              control={control}
              render={({ field }) => (
                <Field
                  required
                  label={t('signUp.password')}
                  invalid={!!errors.password}
                  errorText={errors.password?.message}
                >
                  <Input
                    {...field}
                    required
                    id='password'
                    name='password'
                    type='password'
                    autoComplete='new-password'
                    placeholder={t('signUp.passwordPlaceholder')}
                  />
                </Field>
              )}
            />

            <Controller
              name='fName'
              control={control}
              render={({ field }) => (
                <Field required label={t('signUp.firstName')} invalid={!!errors.fName}>
                  <Input
                    {...field}
                    id='fName'
                    name='fName'
                    type='text'
                    autoComplete='given-name'
                    required
                    placeholder={t('signUp.firstNamePlaceholder')}
                  />
                </Field>
              )}
            />

            <Controller
              name='lName'
              control={control}
              render={({ field }) => (
                <Field label={t('signUp.lastName')} invalid={!!errors.lName}>
                  <Input
                    {...field}
                    id='lName'
                    name='lName'
                    type='text'
                    autoComplete='family-name'
                    placeholder={t('signUp.lastNamePlaceholder')}
                  />
                </Field>
              )}
            />

            <Controller
              name='phone'
              control={control}
              render={({ field }) => (
                <Field required label={t('signUp.phoneNumber')} errorText={errors.phone?.message}>
                  <PhoneNumberInput {...field} placeholder={t('signUp.phoneNumberPlaceholder')} defaultCountry='GB' />
                </Field>
              )}
            />
          </>
        )}
      </Stack>

      {showSignUpFields && (
        <Flex align='center' justify='flex-start' mt='8'>
          <Link href='#' colorPalette='brand' variant='underline' onClick={onBackToEmail}>
            {t('signUp.backToEmail')}
          </Link>
        </Flex>
      )}

      <Button
        type='submit'
        mt='8'
        w='full'
        loading={isSubmitting || checkingSSO}
        loadingText={checkingSSO ? t('signUp.checking') : t('signUp.signingUp')}
        disabled={Object.keys(errors).length > 0}
      >
        {showSignUpFields ? t('signUp.submit') : t('signUp.continue')}
      </Button>
      <Text fontSize='xs' color={mode('gray.600', 'gray.400')} mt='8' textAlign='center'>
        <Trans
          i18nKey='signIn.termsAcknowledge'
          t={t}
          components={{
            terms: (
              <Link
                href='/legal/terms/'
                target='_blank'
                rel='noopener noreferrer'
                colorPalette='brand'
                variant='underline'
              />
            )
          }}
        />
      </Text>
    </form>
  );
};

export default SignUpForm;
